'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getAdventureById } from '@/lib/data'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/free-mode'
import styles from '@/styles/adventure-page.module.css'
import { BreadcrumbSchema, AdventureSchema } from '@/lib/schema'
import { useSwiperCleanup } from '@/hooks/useSwiperCleanup'
import GoogleMapEmbed, { GOOGLE_MAPS_EMBEDS } from '@/components/GoogleMapEmbed'
import BookNowCTA from '@/components/BookNowCTA'


export default function FlyFishingPage() {
  const swiperRef = useSwiperCleanup()
  const thisAdventure = getAdventureById('fly-fishing')
  const t = useTranslations('guidedAdventures')
  const tAdv = useTranslations('adventures')
  const tCommon = useTranslations('common')
  const tLodges = useTranslations('lodges')
  const params = useParams()
  const locale = params.locale as string

  if (!thisAdventure) {
    return <div>{t('common.adventureNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/guided-adventures/` },
        { name: tAdv('fly-fishing.title'), href: `/${locale}/guided-adventures/fly-fishing/` },
      ]} />
      <AdventureSchema
        name={t('flyFishing.schema.name')}
        description={t('flyFishing.schema.description')}
        image={thisAdventure.heroImage}
        url={`/${locale}/guided-adventures/fly-fishing/`}
        activityType={t('flyFishing.schema.activityType')}
        location={t('flyFishing.schema.location')}
        geo={{ latitude: -43.23, longitude: -72.35 }}
      />
      <Header />

      <HeroParallax
        backgroundImage={thisAdventure.heroImage}
        title={tAdv('fly-fishing.title')}
        subtitle={tAdv('fly-fishing.subtitle')}
        location={tAdv('fly-fishing.location')}
      />

      <main className={styles.pageWrapper}>
        <section className={styles.adventureDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              <div className={styles.itineraryColumn}>
                <h3>{t('common.itinerary')}</h3>
                <p>{t('flyFishing.para1')}</p>
                <p>{t('flyFishing.para2')}</p>
              </div>

              <div className={styles.mapColumn}>
                <div className={styles.mapWrapper}>
                  <GoogleMapEmbed
                    {...GOOGLE_MAPS_EMBEDS['yelcho']}
                    
                  />
                </div>
              </div>

              <div className={styles.includedColumn}>
                <h3>{t('flyFishing.whatYouCanBook')}</h3>
                <ul>
                  <li>{t('flyFishing.included.item1')}</li>
                  <li>{t('flyFishing.included.item2')}</li>
                  <li>{t('flyFishing.included.item3')}</li>
                  <li>{t('flyFishing.included.item4')}</li>
                  <li>{t('flyFishing.included.item5')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="inline"
          productId="fly-fishing"
          productType="adventure"
          productName={tAdv('fly-fishing.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/fly-fishing"
        />

        <section className={styles.adventures}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('common.personalizedAdventures')}</h2>
              <p className={styles.sectionDescription}>
                {t('common.personalizedDescription')}
              </p>
            </div>

            <div className={styles.adventuresCarousel}>
              <Swiper
                modules={[Navigation]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: `.${styles.swiperButtonNext}`,
                  prevEl: `.${styles.swiperButtonPrev}`,
                }}
                breakpoints={{
                  640: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                onSwiper={(swiper) => { swiperRef.current = swiper }}
                className={styles.adventuresSwiper}
              >
                {[
                  { id: 1, src: '/images/adventures/fly-fishing/2.webp', alt: t('flyFishing.images.img1') },
                  { id: 2, src: '/images/adventures/fly-fishing/3.webp', alt: t('flyFishing.images.img2') },
                  { id: 3, src: '/images/adventures/fly-fishing/4.webp', alt: t('flyFishing.images.img3') },
                  { id: 4, src: '/images/adventures/fly-fishing/5.webp', alt: t('flyFishing.images.img4') },
                  { id: 5, src: '/images/adventures/fly-fishing/6.webp', alt: t('flyFishing.images.img5') },
                ].map((image) => (
                  <SwiperSlide key={`fly-fishing-${image.id}`}>
                    <article className={styles.adventureCard}>
                      <div className={styles.adventureImage}>
                        <img src={image.src} alt={image.alt} loading="lazy" />
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className={`${styles.swiperButtonPrev} swiper-button-prev`}></div>
              <div className={`${styles.swiperButtonNext} swiper-button-next`}></div>
            </div>

            <div className={styles.sectionCta}>
              <a href={`/${locale}/guided-adventures/`} className="btn-primary">
                {t('common.exploreAdventures')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="section"
          productId="fly-fishing"
          productType="adventure"
          productName={tAdv('fly-fishing.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/fly-fishing"
        />

        <section className={styles.creamSection}>
          <div className="container">
            <h2>{t('common.whereToStay')}</h2>
            <div className={styles.lodgesGrid}>
              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_1-1-qtpgs4svvtox39co7ll9fi4dr08v34gfk5agnwmmq0.avif"
                    alt={`${tLodges('apex-yelcho-patagonia.name')} - ${tLodges('apex-yelcho-patagonia.subtitle')}`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-yelcho-patagonia.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-yelcho-patagonia.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-yelcho-patagonia/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>

              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-patagonia-lodge/Copia-de-6Las-Gualas--768x576.avif"
                    alt={`${tLodges('apex-patagonia-lodge.name')} - ${tLodges('apex-patagonia-lodge.subtitle')}`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-patagonia-lodge.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-patagonia-lodge.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-patagonia-lodge/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="closing"
          productId="fly-fishing"
          productType="adventure"
          productName={tAdv('fly-fishing.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/fly-fishing"
        />

      </main>

      <Footer />
    </>
  )
}
